/* istanbul ignore file */
// libs
import React, { useEffect } from 'react';
import { useShowContext } from 'framework/show';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Box from '@mui/material/Box';
import useTimelineLayoutHelper from 'domain/shared/timeline/layout/BaseTimelineRecordLayout/timelineLayoutHelper';
import PersonTimelineRecordHeader from './PersonTimelineRecordHeader';
import StepContent from '@mui/material/StepContent';
import PersonTimelineRecordActions from './PersonTimelineRecordActions';
import { HtmlField } from 'framework/ui/field';
import { AttachmentsListField, DocumentStorageLoadListField } from 'domain/shared/artifact/field';
import TimelineRecordFooter from 'domain/shared/timeline/layout/BaseTimelineRecordLayout/TimelineRecordFooter';
import { TimelineCollapsible } from 'framework/ui/timeline';
import { scrollToHash } from 'framework/utils';
import { FormFieldsData } from 'framework/form';
import { Typography } from '@mui/material';
import { EVALUATION_CRITERION, EVALUATION_CRITERION_ALIAS } from '../../constants';
import { CandidateTimelineType, EXPECTED_COMPENSATION } from 'domain/shared/candidate';
import { getPersonTimelineIconSx, personTimelineStepContentSx } from './personTimelineRecordSx';
import { timelineTextContainerSx } from 'domain/shared/timeline/layout/BaseTimelineRecordLayout/baseTimelineRecordSx';

const SCROLL_OFFSET = 100;

interface TimelineIcons {
  [key: string]: React.ComponentType<any>;
}

interface FeedbackGrade {
  description?: string;
  attachmentsIds?: string[];
}

interface FeedbackGrades {
  [key: string]: FeedbackGrade;
}

export interface BasePersonTimelineRecordLayoutProps {
  fields?: React.ReactNode;
  actionName: string;
  actions?: React.ReactNode[];
  buttons?: React.ReactNode[];
  updateComponent?: React.ReactNode;
  isEditing?: boolean;
  isHighlighted?: boolean;
  artifactsSource?: string;
  noteTextSource?: string;
  basePathname?: string;
  timelineHeader?: React.ElementType;
  timelineFooter?: React.ElementType;
  timelineActions?: React.ElementType;
  timelineType?: string | ((entity: any) => string);
  timelineIcons: TimelineIcons;
  systemNoteType?: string;
  messageContext: string;
  showDetailLink?: boolean;
  isCandidateService?: boolean;
  isProjectService?: boolean;
  isRiskService?: boolean;
  isStaffingService?: boolean;
  isDocumentStorage?: boolean;
  isShowButtons?: (data: FormFieldsData) => boolean;
  [p: string]: any;
}

const BasePersonTimelineRecordLayout: React.FC<BasePersonTimelineRecordLayoutProps> = ({
  fields = null,
  actionName,
  actions = null,
  buttons = [],
  updateComponent = null,
  isEditing = false,
  isHighlighted = false,
  artifactsSource = 'attachmentsIds',
  noteTextSource = 'noteText',
  basePathname = '',
  timelineHeader: TimeLineHeader = PersonTimelineRecordHeader,
  timelineFooter: TimeLineFooter = TimelineRecordFooter,
  timelineActions: TimelineActions = PersonTimelineRecordActions,
  timelineType: initialTimelineType = 'Note',
  timelineIcons,
  systemNoteType = 'SystemNote',
  isCandidateService = true,
  isProjectService = false,
  isRiskService = false,
  isStaffingService = false,
  isDocumentStorage,
  isShowButtons,
}) => {
  const { data: entity } = useShowContext();
  const subject = entity.getValueBySource('subject');
  const description = entity.getValueBySource('description');
  const timelineId = entity.getValueBySource('timelineId');
  const timelineType = typeof initialTimelineType === 'function' ? initialTimelineType(entity) : initialTimelineType;

  const feedbackGrades = (entity.getValueBySource('feedbackGrades') || {}) as FeedbackGrades;

  const attachmentsIds = entity.getValueBySource(artifactsSource);

  const { getButtons } = useTimelineLayoutHelper({ isShowButtons });

  const IconComponent = timelineIcons[timelineType as CandidateTimelineType] || timelineIcons.default;
  const isSystemNote = timelineType === systemNoteType;

  const AttachmentsComponent = isDocumentStorage ? DocumentStorageLoadListField : AttachmentsListField;

  const entryRef = React.createRef();

  useEffect(() => {
    if (isHighlighted) {
      scrollToHash(entryRef.current, SCROLL_OFFSET);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHighlighted]);

  let fieldContent = fields;
  if (!fieldContent && entity) {
    const feedbackText = entity.getValueBySource('feedbackText');
    if (feedbackText) {
      fieldContent = (
        <TimelineCollapsible>
          <HtmlField value={feedbackText} />
        </TimelineCollapsible>
      );
    }

    const noteText = entity.getValueBySource(noteTextSource);
    if (noteText) {
      fieldContent = (
        <TimelineCollapsible>
          <HtmlField value={noteText} />
        </TimelineCollapsible>
      );
    }

    const expectedCompensationText = entity.getValueBySource('compensationId');
    const type = entity.getValueBySource('type');

    if (expectedCompensationText && type === EXPECTED_COMPENSATION) {
      fieldContent = (
        <TimelineCollapsible>
          <Typography>{`Compensation Id: ${expectedCompensationText}`}</Typography>
        </TimelineCollapsible>
      );
    }
  }

  return (
    <Step sx={{ width: '100%' }} key={`entry${timelineId}`} ref={entryRef as React.RefObject<HTMLDivElement>} active>
      <StepLabel
        sx={{ padding: 0 }}
        icon={
          <Box sx={getPersonTimelineIconSx(isSystemNote, isHighlighted)}>
            <IconComponent />
          </Box>
        }
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            backgroundColor: isSystemNote ? 'background.default' : 'background.primary',
            ...(isHighlighted && { boxShadow: '0 2px 4px var(--mui-palette-common-shadowStrong)' }),
          }}
        >
          <>
            <Box title={timelineType} />
            <TimeLineHeader actionName={actionName} timelineId={timelineId} entity={entity} />
            <TimelineActions
              entity={entity}
              buttons={getButtons(Array.isArray(buttons) ? (buttons.filter(Boolean) as React.ReactElement[]) : [])}
              actions={actions}
              timelineId={timelineId || entity.id}
              basePathname={basePathname}
            />
          </>
        </Box>
      </StepLabel>
      <StepContent sx={personTimelineStepContentSx}>
        {isEditing && updateComponent ? (
          updateComponent
        ) : (
          <>
            <Box sx={timelineTextContainerSx}>
              {subject?.trim() || fieldContent ? (
                <Box>
                  {subject ? (
                    <Box fontSize={18} pr={5.6}>
                      <HtmlField value={subject} />
                    </Box>
                  ) : null}
                  {fieldContent}
                </Box>
              ) : null}
              {description && (
                <Box>
                  <TimelineCollapsible>
                    <HtmlField value={description} />
                  </TimelineCollapsible>
                </Box>
              )}
            </Box>
            {attachmentsIds && attachmentsIds.length ? (
              <AttachmentsComponent
                isCandidateService={isCandidateService}
                isProjectService={isProjectService}
                isRiskService={isRiskService}
                isStaffingService={isStaffingService}
                value={attachmentsIds}
              />
            ) : null}
            {feedbackGrades ? (
              <Box>
                {Object.entries(feedbackGrades).map(([key, value]) => {
                  const evaluationCriteria = EVALUATION_CRITERION.find(
                    item => item.value === key && item.value !== EVALUATION_CRITERION_ALIAS.NOT_ABOUT_CRITERION,
                  );
                  return value.description ? (
                    <Box key={key} mb="10px">
                      <Typography variant="body1" component="span">
                        {evaluationCriteria?.label}
                      </Typography>
                      {value.description ? <HtmlField label="" value={value.description} /> : null}
                      {value.attachmentsIds && value.attachmentsIds.length ? (
                        <AttachmentsListField value={value.attachmentsIds} />
                      ) : null}
                    </Box>
                  ) : null;
                })}
              </Box>
            ) : null}
            <TimeLineFooter entity={entity} noTopIndent={!attachmentsIds || !attachmentsIds.length} />
          </>
        )}
      </StepContent>
    </Step>
  );
};

export default BasePersonTimelineRecordLayout;
